import React, { useState, useRef, useEffect } from 'react';
import { OpenVidu, Publisher, Session, Subscriber } from 'openvidu-browser';
import { OpenViduContext } from './OpenViduContext';
import { getSearchParams } from '../tools/getSearchParams';
import { Logger } from '../tools/logger';
import { useLayoutEffect } from 'react';
import { RNBridge } from '../tools/RNBridge';


const console = new Logger('OpenViduProvider', true)
/** 
 * @template T
 * @typedef {ReturnType<typeof useState<T>>} UseState
 */

export const OpenViduProvider = ({ children }) => {
  /** @type {UseState<Session>} */
  const [session, setSession] = useState(null);
  /** @type {UseState<Publisher>} */
  const [publisher, setPublisher] = useState(null);
  /** @type {UseState<Subscriber[]>} */
  const [subscribers, setSubscribers] = useState([]);
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  /** @type {UseState<'camera' | 'screen'>} */
  const [mediaSource, setMediaSource] = useState('camera'); // 'camera', 'camera' or 'screen'
  const [isFrontCamera, setIsFrontCamera] = useState(true)
  
  /** @type {React.RefObject<OpenVidu>} */
  const OV = useRef(null)
  useLayoutEffect(() => {
    OV.current = new OpenVidu()
  }, [])

  // Detect mobile device
  useEffect(() => {
    const checkMobile = () => {
      const mobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      setIsMobile(mobile);
      // Set default media source based on device
      setMediaSource(mobile ? 'camera' : 'screen');
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const getToken = () => {
    const token = getSearchParams('token')
    if (token) {
      return token;
    }
    throw new Error('token not found')
  };
  const getSessionId = () => {
    const sessionId = getSearchParams('sessionId')
    if (sessionId) {
      return sessionId;
    }
    throw new Error('sessionId not found')
  }
  const isScreenShareActive =  useRef(false)

  /**
   * @param {{
   *  videoSource?: string
   *  mirror?: boolean
   * }} [params]
   */
  const initPublisher = async (params) => {
    const publisherOptions = {
      audioSource: undefined, // Default microphone
      publishAudio: true,
      resolution: '640x480',
      frameRate: 30,
      insertMode: 'APPEND',
    };

    if (mediaSource === 'screen' && !isMobile) {
      // Screen sharing for desktop
      publisherOptions.videoSource = 'screen';
      publisherOptions.publishVideo = true;
      publisherOptions.mirror = false;
      
      try {
        const screenPublisher = OV.current.initPublisher(undefined, publisherOptions);
        
        screenPublisher.on('streamCreated', () => {
          console.log('🖥️ Screen share stream created');
        });
        
        screenPublisher.on('streamDestroyed', () => {
          console.log('🖥️ Screen share stream destroyed');
        });
        
        isScreenShareActive.current = true
        return screenPublisher;
      } catch (screenError) {
        console.warn('Screen sharing failed, falling back to camera:', screenError);
        // Fallback to camera
        // setMediaSource('camera');
      }
    }

    // Camera for mobile or fallback
    publisherOptions.videoSource = params?.videoSource || undefined; // Default camera
    publisherOptions.publishVideo = true;
    publisherOptions.mirror = params?.mirror ?? !!isMobile; // Mirror for front camera on mobile
    console.log('publisher options:', publisherOptions)
    
    const cameraPublisher = OV.current.initPublisher(undefined, publisherOptions);
    
    cameraPublisher.on('accessAllowed', () => {
      console.log('✅ Camera and microphone access allowed');
    });

    cameraPublisher.on('accessDenied', () => {
      console.error('❌ Camera and microphone access denied');
      setError('Camera and microphone access is required for video calls');
    });

    return cameraPublisher;
  };

  useEffect(() => {
    if (session && subscribers[0] && publisher) {
      const parse = (data) => {
        try {
          const json = data.split('%/%')[0]
          return JSON.parse(json)
        } catch (e) {
          return null
        }
      }
      session.signal({
        type: 'userChanged',
        data: JSON.stringify({
          isAudioActive: publisher.stream?.audioActive,
          isVideoActive: publisher.stream?.videoActive,
          nickname: parse(publisher.stream?.connection?.data)?.clientData ?? 'Patient',
          isScreenShareActive: isScreenShareActive.current,
        })
      })
    }
  }, [publisher, session, subscribers.length])

  const joinSession = async () => {
    const sessionId = getSessionId()
    if (isConnected) {
      console.log('⚠️ Already connected to session');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      console.log('🚀 Joining session:', sessionId);
      
      const token = getToken();
      
      // Initialize OpenVidu Session
      const newSession = OV.current.initSession();
      setSession(newSession);

      // Set up event handlers
      newSession.on('streamCreated', (event) => {
        console.log('📹 Stream created:', event.stream.streamId);
        const subscriber = newSession.subscribe(event.stream, undefined);
        setSubscribers(prev => [...prev, subscriber]);
      });

      newSession.on('streamDestroyed', (event) => {
        console.log('📹 Stream destroyed:', event.stream.streamId);
        setSubscribers(prev => prev.filter(sub => sub.stream.streamId !== event.stream.streamId));
      });

      // eslint-disable-next-line no-unused-vars
      newSession.on('sessionDisconnected', (event) => {
        console.log('🔌 Session disconnected');
        setIsConnected(false);
        setSession(null);
        setPublisher(null);
        setSubscribers([]);
      });

      newSession.on('exception', (exception) => {
        console.warn('⚠️ OpenVidu exception:', exception);
      });

      // Connect to the session
      console.log('🔗 Connecting to session with token...');
      await newSession.connect(token, { 
        name: isMobile ? 'Mobile User' : 'Desktop User',
        mediaSource: mediaSource,
        joinTime: new Date().toISOString()
      });

      console.log('✅ Successfully connected to session');

      // Initialize and publish stream
      const newPublisher = await initPublisher();
      await newSession.publish(newPublisher);
      setPublisher(newPublisher);
      setIsConnected(true);
      setMediaSource(isScreenShareActive.current ? 'screen' : 'camera')
      setTimeout(() => RNBridge.sendLocalUser(), 500)
      
      console.log('🎉 Successfully joined session and published stream');

    } catch (error) {
      console.error('❌ Failed to join session:', error);
      setError(error.message);
      
      // Clean up on error
      // if (session) {
      //   session.disconnect();
      // }
      RNBridge.sendConnectionFailed(error.message)
      setSession(null);
      setPublisher(null);
      setSubscribers([]);
      setMediaSource(isScreenShareActive.current ? 'screen' : 'camera')
      setIsConnected(false);
    } finally {
      setIsLoading(false);
    }
  };

  const switchMediaSource = async (newSource) => {
    if (!isConnected || !session || !publisher) return;
    
    if (newSource === mediaSource) return;
    
    try {
      console.log(`🔄 Switching media source from ${mediaSource} to ${newSource}`);
      
      // Unpublish current stream
      await session.unpublish(publisher);
      
      // Set new media source
      setMediaSource(newSource);
      
      // Initialize new publisher
      const newPublisher = await initPublisher();
      
      // Publish new stream
      await session.publish(newPublisher);
      setPublisher(newPublisher);
      
      console.log(`✅ Successfully switched to ${newSource}`);
    } catch (error) {
      console.error('❌ Failed to switch media source:', error);
      setError('Failed to switch media source');
    }
  };

  const leaveSession = () => {
    console.log('👋 Leaving session');
    
    if (session) {
      session.disconnect();
    }
    
    setSession(null);
    setPublisher(null);
    setSubscribers([]);
    setIsConnected(false);
    setError(null);
  };

  const toggleAudio = () => {
    if (publisher) {
      const newState = !publisher.stream.audioActive;
      publisher.publishAudio(newState);
      console.log('🎤 Audio', newState ? 'enabled' : 'disabled');
      return newState
    }
  };

  const toggleVideo = () => {
    if (publisher) {
      const newState = !publisher.stream.videoActive;
      publisher.publishVideo(newState);
      console.log('📹 Video', newState ? 'enabled' : 'disabled');
      return newState
    }
  };

  const clearError = () => {
    setError(null);
  };
  const getLocalUser = () => {
    if (publisher) {
      const isVideoActive = publisher.stream.videoActive;
      const isAudioActive = publisher.stream.audioActive;
      return {
        isAudioActive,
        isVideoActive,
        mirror: isFrontCamera,
      }
    }
  }
  const isSwitchingCamera = useRef(false)
  const switchCamera = async () => {
    if (!isConnected || !session || !publisher || isSwitchingCamera.current) {
      return;
    }
    try {
      isSwitchingCamera.current = true
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter(device => device.kind === 'videoinput')
      if (videoDevices.length <= 1) {
        return;
      }
      const mediaStream = publisher.stream.getMediaStream()
      const currentVideoTrack = mediaStream.getVideoTracks()[0]
      const currentVideoDevice = videoDevices.find(device => device.label === currentVideoTrack.label)
      const nextVideoDevice = videoDevices.find(device => device.deviceId !== currentVideoDevice?.deviceId)

      session.unpublish(publisher)

      const newPublisher = await initPublisher({
        videoSource: nextVideoDevice.deviceId,
        mirror: !isFrontCamera,
      })
      
      // Publish new stream
      await session.publish(newPublisher);
      setPublisher(newPublisher);
      setIsFrontCamera((v) => !v)
      setTimeout(() => RNBridge.sendLocalUser(), 500)

    } finally {
      isSwitchingCamera.current = false
    }
  }

  const value = {
    session,
    publisher,
    subscribers,
    isConnected,
    isLoading,
    error,
    isMobile,
    mediaSource,
    joinSession,
    leaveSession,
    toggleAudio,
    toggleVideo,
    switchMediaSource,
    clearError,
    getLocalUser,
    switchCamera,
  };
  RNBridge.context = value

  return (
    <OpenViduContext.Provider value={value}>
      {children}
    </OpenViduContext.Provider>
  );
};
